import { createContext, useReducer } from 'react';
import { deleteFromIndexedDB, clearIndexedDB } from './indexedDB';

export const Store = createContext();
// the Store.js is used for local storage (the users browser storage that can safe the data across different sessions, to stay logged in for exaple), session storage (application state, that safes the data only for the current session), and cookies in web browsers (for exaple the items in the cart)
// reactContext needs a Store (React Context allows data to be passed down through the component tree without the need to pass props manually at every level)

export const loadInitialState = () => ({
  // the default states
  userInfo: localStorage.getItem('userInfo')
    ? JSON.parse(localStorage.getItem('userInfo'))
    : null, // gets the state of the userInfo from the localStorage (because maybe the user already signed-in, in another session before) and converts the string to a js object. If no userInfo exists in the localStorage, the state will be null

  cart: {
    shippingAddress: localStorage.getItem('shippingAddress')
      ? JSON.parse(localStorage.getItem('shippingAddress'))
      : {}, // gets the state of the shippingAddress from the localStorage (because maybe the user already entered his address somewhere before, in another session) and converts the string to a js object. If no shippingAddress exist in the localStorage, the state will be an empty object

    billingAddress: localStorage.getItem('billingAddress')
      ? JSON.parse(localStorage.getItem('billingAddress'))
      : {}, // gets the state of the billingAddress from the localStorage (because maybe the user already entered his address somewhere before, in another session) and converts the string to a js object. If no billingAddress exist in the localStorage, the state will be an empty object

    paymentMethod: localStorage.getItem('paymentMethod')
      ? localStorage.getItem('paymentMethod')
      : '', // gets the state of the paymentMethod from the localStorage (because maybe the user already entered the paymentMethod somewhere before, in another session). If no paymentMethod exist in the localStorage, the state will be an empty string

    cartItems: localStorage.getItem('cartItems')
      ? JSON.parse(localStorage.getItem('cartItems'))
      : [], // gets the state of the cartItems from the localStorage (because maybe the user already put items in the cart, in another session before) and converts the string to a js object. If no cartItems exist in the localStorage, the state will be an empty array
  },
}); // a function only so the tests can call it, the store still reads localStorage once, when this module loads

const initialState = loadInitialState();

export function reducer(state, action) {
  // reducer function to update the state for different cases
  switch (action.type) {
    case 'CART_ADD_ITEM':
      const newItem = action.payload; // the payload sent from the frontend (product, unique id, design state, thumbnail, ...)
      let cartItems;

      if (newItem.designId) {
        // 1) Handle the MongoDB-based design item
        const existItem = state.cart.cartItems.find(
          (item) => item.designId === newItem.designId
        );

        if (existItem) {
          // Increment the quantity if item already exists
          cartItems = state.cart.cartItems.map((item) =>
            item.designId === existItem.designId
              ? {
                  ...newItem,
                  quantity: existItem.quantity + newItem.quantity, // Increment quantity
                }
              : item
          );
        } else {
          // If this DB-based design doesn't exist in cart yet, add it
          cartItems = [...state.cart.cartItems, newItem];
        }
      } else {
        cartItems = [...state.cart.cartItems, newItem]; // 2) An IndexedDB-based design item is always new: the customizer gives every add a fresh uniqueCartId, also to a design loaded from a cart item. A branch replacing an item with the same id, and a fallback for an item with neither id, went unused and were deleted Oct 2026; an item sent again with an existing id would now make a second row on the same IndexedDB asset, which a remove deletes for both rows and an update replaces in both
      }

      localStorage.setItem('cartItems', JSON.stringify(cartItems)); // safes the updated cartItems in the localStorage of the users Browser (in this case, the localStorage is being modified here and not in the event handler function in the CartScreen, because the cart items stored in localStorage directly related to the state management in the Store. This means that the data necessary to the localStorage 100% correlates with the data necessary to the application state)

      return { ...state, cart: { ...state.cart, cartItems } }; // updates the cart in the application state

    case 'CART_UPDATE_ITEM': {
      const updatedItem = action.payload;
      let cartItems;

      if (updatedItem.uniqueCartId) {
        // If it’s an IndexedDB-based design
        const existItem = state.cart.cartItems.find(
          (i) => i.uniqueCartId === updatedItem.uniqueCartId
        );

        if (existItem) {
          cartItems = state.cart.cartItems.map((item) =>
            item.uniqueCartId === updatedItem.uniqueCartId ? updatedItem : item
          ); // Replace that item
        } else {
          cartItems = [...state.cart.cartItems, updatedItem]; // If for some reason it doesn't exist, add it
        }
      } else if (updatedItem.designId) {
        // If it’s a DB-based design
        const existItem = state.cart.cartItems.find(
          (i) => i.designId === updatedItem.designId
        );

        if (existItem) {
          cartItems = state.cart.cartItems.map((item) =>
            item.designId === updatedItem.designId ? { ...updatedItem } : item
          ); // Replace that item - do NOT increment
        } else {
          cartItems = [...state.cart.cartItems, updatedItem]; // If not found, add it
        }
      } else {
        cartItems = [...state.cart.cartItems, updatedItem]; // Fallback if neither uniqueCartId nor designId
      }

      localStorage.setItem('cartItems', JSON.stringify(cartItems));
      return { ...state, cart: { ...state.cart, cartItems } };
    }

    case 'CART_REMOVE_ITEM': {
      const toRemove = action.payload;
      let filteredItems;

      if (toRemove.uniqueCartId) {
        // It's an IndexedDB-based design
        filteredItems = state.cart.cartItems.filter(
          (item) => item.uniqueCartId !== toRemove.uniqueCartId
        );
        deleteFromIndexedDB(toRemove.uniqueCartId); // Also remove from IndexedDB
      } else if (toRemove.designId) {
        // It's a design with a design _id reference to the MongoDB
        filteredItems = state.cart.cartItems.filter(
          (item) => item.designId !== toRemove.designId
        );
      } else {
        filteredItems = [...state.cart.cartItems]; // Fallback (unlikely scenario)
      }

      localStorage.setItem('cartItems', JSON.stringify(filteredItems)); // safes the updated cartItems in the localStorage of the users Browser (in this case, the localStorage is being modified here and not in the event handler function in the CartScreen, because the cart items stored in localStorage directly related to the state management in the Store. This means that the data necessary to the localStorage 100% correlates with the data necessary to the application state)
      return { ...state, cart: { ...state.cart, cartItems: filteredItems } }; // updates the cart in the application state
    }

    case 'CART_CLEAR':
      clearIndexedDB(); // clear all designs and thumbnails from the indexedDB
      localStorage.removeItem('cartItems'); // Clear metadata in localStorage
      return { ...state, cart: { ...state.cart, cartItems: [] } }; // set the cartItems to an empty array (clears all items from the cart)

    case 'USER_SIGNIN':
      // the update to the localStorage is made in the frontend screen where 'USER_SIGNIN' is called
      return { ...state, userInfo: action.payload }; // updates the userInfo in the application state, based on the action.payload (the user data from the backend)

    case 'USER_SIGNOUT':
      // the update to the localStorage is made in the frontend screen where 'USER_SIGNOUT' is called
      return {
        ...state,
        userInfo: null,
        cart: {
          ...state.cart,
          shippingAddress: {},
          billingAddress: {},
          paymentMethod: '',
        },
      }; // resets the userInfo, shippingAddress, billingAddress and paymentMethod of the application state

    case 'SAVE_SHIPPING_ADDRESS':
      // the update to the localStorage is made in the frontend screen where 'SAVE_SHIPPING_ADDRESS' is called
      return {
        ...state,
        cart: {
          ...state.cart,
          shippingAddress: action.payload,
        },
      }; // updates/adds the shippingAddress in the cart's application state, based on the action.payload

    case 'SAVE_BILLING_ADDRESS':
      // the update to the localStorage is made in the frontend screen where 'SAVE_BILLING_ADDRESS' is called
      return {
        ...state,
        cart: {
          ...state.cart,
          billingAddress: action.payload,
        },
      }; // updates/adds the billingAddress in the cart's application state, based on the action.payload

    case 'SAVE_PAYMENT_METHOD':
      // the update to the localStorage is made in the frontend screen where 'SAVE_PAYMENT_METHOD' is called
      return {
        ...state,
        cart: {
          ...state.cart,
          paymentMethod: action.payload,
        },
      }; // updates/adds the paymentMethod in the cart's application state, based on the action.payload

    default:
      return state;
  }
}

export function StoreProvider(props) {
  // StoreProvider is a wrapper of the react application and passes global props to its children (in being used in index.js)
  const [state, dispatch] = useReducer(reducer, initialState);
  const value = { state, dispatch };
  return <Store.Provider value={value}>{props.children}</Store.Provider>;
}
